/**
 * Observability configuration resolution.
 *
 * Reads Axiom credentials from the environment. When credentials are absent the
 * telemetry client becomes a no-op that only logs to the console, so nothing in
 * the application ever fails because observability isn't configured.
 */

export interface ObservabilityConfig {
  /** If true, structured events are actually shipped to Axiom. */
  enabled: boolean;
  /** Axiom API token (AXIOM_TOKEN). */
  token: string | undefined;
  /** Axiom dataset to ingest into (AXIOM_DATASET). */
  dataset: string | undefined;
  /** Optional overridden ingest endpoint (for local/mock Axiom). */
  ingestUrl: string | undefined;
  /** Service name, e.g. "web", "api". */
  service: string;
  /** Service version. */
  version: string;
  /** Environment, e.g. "development", "production". */
  environment: string;
  /** Logs everything to the console too (true in development). */
  console: boolean;
  /** Ingest batch flush interval in ms. */
  flushIntervalMs: number;
}

export interface ObservabilityEnv {
  AXIOM_TOKEN?: string;
  AXIOM_DATASET?: string;
  AXIOM_INGEST_URL?: string;
  NODE_ENV?: string;
}

const AXIOM_INGEST_BASE = "https://api.axiom.co/v1";

/**
 * Read an env value safely in both browser and Node. `import.meta.env` is
 * available under Vite; `process.env` under Node/Bun.
 *
 * `allowVite` must only be true for NON-SECRET keys (e.g. dataset, ingest
 * URL): `VITE_*` variables are inlined into the public browser bundle, so
 * secrets like `AXIOM_TOKEN` must never be read that way — they are only
 * ever read from `process.env` server-side.
 */
function readEnv(key: string, allowVite = false): string | undefined {
  if (allowVite) {
    const metaEnv = (import.meta as unknown as { env?: Record<string, unknown> })?.env;
    if (metaEnv) {
      const meta = metaEnv[`VITE_${key}`] ?? metaEnv[key];
      if (typeof meta === "string") return meta;
    }
  }
  const proc = (globalThis as { process?: { env?: Record<string, unknown> } }).process;
  const env = proc?.env?.[key];
  if (typeof env === "string") return env;
  return undefined;
}

export function resolveConfig(
  service: string,
  version = "0.1.0",
  overrides: Partial<ObservabilityConfig> & ObservabilityEnv = {},
): ObservabilityConfig {
  // Overrides apply to the *inputs* first; every derived value (enabled,
  // console, default ingest URL) is then computed from the merged inputs so
  // the returned config is always internally consistent (e.g. `console` can
  // never disagree with `environment`, and env keys never leak as extra
  // properties via a trailing `...overrides` spread).
  const environment =
    overrides.environment ??
    overrides.NODE_ENV ??
    (import.meta as unknown as { env?: { MODE?: string } })?.env?.MODE ??
    readEnv("NODE_ENV") ??
    "development";

  // The token is a secret: never read via `VITE_*` (it would be inlined into
  // the browser bundle). Dataset / ingest URL are non-secret and may come from
  // `VITE_*` for browser-side configuration.
  const token = overrides.token ?? overrides.AXIOM_TOKEN ?? readEnv("AXIOM_TOKEN");
  const dataset =
    overrides.dataset ?? overrides.AXIOM_DATASET ?? readEnv("AXIOM_DATASET", true);
  const ingestUrl =
    overrides.ingestUrl ??
    overrides.AXIOM_INGEST_URL ??
    readEnv("AXIOM_INGEST_URL", true) ??
    (token && dataset ? `${AXIOM_INGEST_BASE}/datasets/${dataset}/ingest` : undefined);

  return {
    enabled: overrides.enabled ?? Boolean(token && dataset && ingestUrl),
    token,
    dataset,
    ingestUrl,
    service,
    version,
    environment,
    console: overrides.console ?? environment === "development",
    flushIntervalMs: overrides.flushIntervalMs ?? 2_000,
  };
}
